import React, { useEffect, useState } from "react";
import { useAuthContext } from "../context/auth";
import { ClockIcon, Pause, Play } from "./Icons";
import { TimerRenderer } from "react-use-precision-timer";
import PortraitScreen from "../PortraitScreen";
import { BasicProgram, CircuitProgram } from "../types";
import cn from "../utils/cn";
import PortraitDefault from "./PortraitDefault";
import { ClubDayBadgesDetailData } from "../Api";

function ProgarmItem({
  title,
  index,
  active,
  onClick,
}: {
  title: string;
  index: number;
  active?: boolean;
  onClick?: () => void;
}) {
  return (
    <div
      data-active={active ? "true" : undefined}
      className={cn(
        "flex gap-[32px] py-[20px] text-[42px] font-bold",
        active && "text-white"
      )}
      onClick={onClick}
    >
      <p>{index + 1}</p>
      <p>{title}</p>
    </div>
  );
}
function ControlPanel({
  clubDayChunks,
}: {
  clubDayChunks: ClubDayBadgesDetailData["classAchievements"][];
}) {
  const {
    circuitProgram,
    basicProgram,
    sendSetStep,
    step,
    timer,
    sendStartTimer,
    sendStartCountDown,
    sendStopCountDown,
    index,
    sendCurrentIndex,
  } = useAuthContext();
  const [stopped, setStopped] = useState(false);
  const program = circuitProgram ?? basicProgram;
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const warmUpRef = React.useRef<HTMLParagraphElement>(null);
  const mainExerRef = React.useRef<HTMLParagraphElement>(null);
  const part1Ref = React.useRef<HTMLParagraphElement>(null);
  const part2Ref = React.useRef<HTMLParagraphElement>(null);
  const coolDownRef = React.useRef<HTMLParagraphElement>(null);
  const badgeRef = React.useRef<HTMLParagraphElement>(null);

  const scrollToCenter = (target: HTMLElement | null) => {
    const container = scrollContainerRef.current;
    if (!container || !target) return;

    const containerRect = container.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    // 앱 전체에 transform: scale(...)이 걸려 있어 getBoundingClientRect는
    // 축소된 좌표를 반환한다. scrollTop/clientHeight는 레이아웃(미축소) 좌표이므로
    // scale로 나눠 좌표계를 맞춰준다.
    const scale = containerRect.height / container.offsetHeight || 1;
    const offset = (targetRect.top - containerRect.top) / scale;
    const top =
      container.scrollTop +
      offset -
      (container.clientHeight - target.offsetHeight) / 2;

    container.scrollTo({ top, behavior: "smooth" });
  };

  const sectionFallback = (): HTMLParagraphElement | null => {
    if (step === 2) return warmUpRef.current;
    if ((step === 4 || step === 3) && program?.programType === "BASIC")
      return mainExerRef.current;
    if ((step === 4 || step === 3) && program?.programType !== "BASIC")
      return part1Ref.current;
    if ((step === 6 || step === 5) && program?.programType !== "BASIC")
      return part2Ref.current;
    if (
      (step === 5 && program?.programType === "BASIC") ||
      (step === 7 && program?.programType !== "BASIC")
    )
      return coolDownRef.current;
    if (
      (step === 6 && program?.programType === "BASIC") ||
      (step === 8 && program?.programType !== "BASIC")
    )
      return badgeRef.current;
    return null;
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const activeItems = container.querySelectorAll<HTMLElement>(
      '[data-active="true"]'
    );

    if (activeItems.length === 1) {
      scrollToCenter(activeItems[0]);
    } else {
      scrollToCenter(sectionFallback());
    }
  }, [step, program, index]);

  useEffect(() => {
    if (step) {
      setStopped(false);
      sendStartCountDown();
    }
  }, [step, sendStartCountDown]);

  if (!program) return <></>;
  return (
    <div className="flex">
      <div
        ref={scrollContainerRef}
        className="flex flex-col py-[80px] px-[60px] gap-[60px] bg-[#2B2316] w-[705px] text-[#A19581] max-h-[1080px] overflow-y-scroll shrink-0"
      >
        <div className="flex gap-[30px] flex-col text-start scroll-m-7">
          <p
            ref={warmUpRef}
            onClick={() => {
              sendSetStep(2);
              sendCurrentIndex(0);
            }}
            className={cn(
              "text-[32px] font-extrabold",
              step === 2 && "gradient-text"
            )}
          >
            WARM UP
          </p>
          <div className="flex flex-col">
            {program.warmUpMovements.map((movement, idx) => (
              <ProgarmItem
                title={movement.movement.name}
                index={idx}
                key={idx}
                active={step === 2 && idx === index}
                onClick={() => {
                  sendSetStep(2);
                  sendCurrentIndex(idx);
                }}
              />
            ))}
          </div>
        </div>
        {program.programType === "BASIC" ? (
          <div className="flex gap-[30px] flex-col text-start scroll-m-7">
            <p
              ref={mainExerRef}
              onClick={() => {
                sendSetStep(3);
              }}
              className={cn(
                "text-[32px] font-extrabold",
                (step === 4 || step === 3) && "gradient-text"
              )}
            >
              MAIN EXER
            </p>
            <div className="flex flex-col">
              {(program as BasicProgram).mainExers.map((movement, idx) => (
                <ProgarmItem
                  title={movement.movement.name}
                  index={idx}
                  key={idx}
                  active={step === 4 && Math.floor((index ?? 0) / 2) === idx}
                  onClick={() => {
                    sendSetStep(4);
                    sendCurrentIndex(idx * 2);
                  }}
                />
              ))}
            </div>
          </div>
        ) : (
          <>
            <div className="flex gap-[30px] flex-col text-start scroll-m-7">
              <p
                ref={part1Ref}
                onClick={() => {
                  sendSetStep(3);
                }}
                className={cn(
                  "text-[32px] font-extrabold",
                  (step === 4 || step === 3) && "gradient-text"
                )}
              >
                PART 1
              </p>
              <div
                className="flex flex-col"
                onClick={() => {
                  sendSetStep(4);
                }}
              >
                {(program as CircuitProgram).part1.movements.map(
                  (movement, index) => (
                    <ProgarmItem
                      title={movement.name}
                      index={index}
                      key={index + "part1"}
                      active={step === 4}
                    />
                  )
                )}
              </div>
            </div>
            <div className="flex gap-[30px] flex-col text-start scroll-m-7">
              <p
                ref={part2Ref}
                onClick={() => {
                  sendSetStep(5);
                }}
                className={cn(
                  "text-[32px] font-extrabold",
                  (step === 6 || step === 5) && "gradient-text"
                )}
              >
                PART 2
              </p>
              <div
                className="flex flex-col"
                onClick={() => {
                  sendSetStep(6);
                }}
              >
                {(program as CircuitProgram).part2.movements.map(
                  (movement, index) => (
                    <ProgarmItem
                      title={movement.name}
                      index={index}
                      key={index + "part2"}
                      active={step === 6}
                    />
                  )
                )}
              </div>
            </div>
          </>
        )}
        <div className="flex gap-[30px] flex-col text-start scroll-m-7">
          <p
            ref={coolDownRef}
            onClick={() => {
              if (program.programType === "BASIC") {
                sendSetStep(5);
              } else {
                sendSetStep(7);
              }
              sendCurrentIndex(0);
            }}
            className={cn(
              "text-[32px] font-extrabold",
              ((step === 5 && program.programType === "BASIC") ||
                (step === 7 && program.programType !== "BASIC")) &&
                "gradient-text"
            )}
          >
            COOL DOWN
          </p>
          <div className="flex flex-col">
            {program.coolDownMovements.map((movement, idx) => (
              <ProgarmItem
                title={movement.movement.name}
                index={idx}
                key={idx}
                active={
                  ((step === 5 && program.programType === "BASIC") ||
                    (step === 7 && program.programType !== "BASIC")) &&
                  idx === index
                }
                onClick={() => {
                  if (program.programType === "BASIC") {
                    sendSetStep(5);
                  } else {
                    sendSetStep(7);
                  }
                  sendCurrentIndex(idx);
                }}
              />
            ))}
          </div>
        </div>

        {clubDayChunks.length > 0 && (
          <div className="flex gap-[30px] flex-col text-start scroll-m-7">
            <p
              ref={badgeRef}
              className={cn(
                "text-[32px] font-extrabold cursor-pointer",
                ((step === 6 && program.programType === "BASIC") ||
                  (step === 8 && program.programType !== "BASIC")) &&
                  "gradient-text"
              )}
              onClick={() => {
                if (program.programType === "BASIC") {
                  sendSetStep(6);
                } else {
                  sendSetStep(8);
                }
                sendCurrentIndex(0);
              }}
            >
              BADGE
            </p>
            <div className="flex flex-col">
              {clubDayChunks.map((_, idx) => (
                <ProgarmItem
                  title={`${idx + 1} / ${clubDayChunks.length}`}
                  index={idx}
                  key={idx}
                  active={
                    ((step === 6 && program.programType === "BASIC") ||
                      (step === 8 && program.programType !== "BASIC")) &&
                    idx === index
                  }
                  onClick={() => {
                    if (program.programType === "BASIC") {
                      sendSetStep(6);
                    } else {
                      sendSetStep(8);
                    }
                    sendCurrentIndex(idx);
                  }}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-col flex-1 relative">
        <div className="flex-1"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
          {step === 1 ? (
            <button
              className="rounded-[32px] text-[100px] text-black py-4 px-[160px] flex justify-center items-center mt-[60px]"
              style={{
                background:
                  "linear-gradient(270deg, #FFEFD6 0%, #FFEFD6 0.01%, #D7B98B 100%)",
              }}
              onClick={() => {
                sendStartTimer();
                sendSetStep(2);
                sendCurrentIndex(0);
              }}
            >
              GO!
            </button>
          ) : (step === 6 && program.programType === "BASIC") ||
            (step === 8 && program.programType !== "BASIC") ? (
            <button
              className="rounded-[32px] text-[100px] text-black py-4 w-[500px] flex justify-center items-center mt-[60px]"
              style={{
                background:
                  "linear-gradient(270deg, #FFEFD6 0%, #FFEFD6 0.01%, #D7B98B 100%)",
              }}
              onClick={() => {
                sendSetStep(0);
              }}
            >
              수업 종료
            </button>
          ) : (
            <button
              onClick={() => {
                if (stopped) {
                  setStopped(false);
                  sendStartCountDown();
                } else {
                  setStopped(true);
                  sendStopCountDown();
                }
              }}
            >
              {stopped ? <Play /> : <Pause />}
            </button>
          )}
        </div>
        <div className="flex justify-center items-center relative">
          <div className="flex gap-4 items-center">
            <ClockIcon />
            <TimerRenderer
              timer={timer}
              render={(t) => (
                <>
                  <div className="text-[#463C2D] flex text-[64px]">
                    <p className="">
                      {String(
                        Math.floor(t.getElapsedRunningTime() / 1000 / 60)
                      ).padStart(2, "0")}
                    </p>
                    <p className=" ">:</p>
                    <p className="">
                      {String(
                        Math.floor((t.getElapsedRunningTime() / 1000) % 60)
                      ).padStart(2, "0")}
                    </p>
                  </div>
                </>
              )}
              renderRate={500} // In milliseconds
            />
          </div>
        </div>
      </div>

      <div className="w-[607.5px] h-[1080px]">
        <div className="w-full relative">
          <div
            className="relative w-full"
            style={{ paddingTop: `${(1920 / 1080) * 100}%` }}
          >
            <div className="absolute top-0 left-0 w-full h-full">
              <div
                className="w-full h-full"
                style={{
                  transform: `scale(${607.5 / 1080})`,
                  transformOrigin: "top left",
                }}
              >
                <div className="w-[1080px] h-[1920px]">
                  <PortraitDefault disabledLogout />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ControlPanel;
